

















































































































































import { mapGetters, mapState } from 'vuex'
import { format, timeDistanceFromNow } from '~/utils/date'
import CMessageAvatar from './CMessageAvatar.vue'
import { capitalizeString } from '~/utils/string'
import { USER_NS } from '~/store/modules/shared/user/state'
import CConfirmationModal from '~/components/shared/configurable/modal/CConfirmationModal.vue'
import ClassifiedOfferPrice from '~/components/car/offers/ClassifiedOfferPrice.vue'
import { USER_AGENT_NS } from '~/store/modules/shared/userAgent/state'
import { defineComponent } from '~/utils/nuxt3-migration'
import CMiniMessageAttachments from '~/components/shared/configurable/message/CMiniMessageAttachments.vue'
import { ciTrash } from '~/icons/source/solid/trash'
import { ciExclamationTriangle } from '~/icons/source/solid/exclamation-triangle'

export default defineComponent({
  components: {
    ClassifiedOfferPrice,
    CConfirmationModal,
    CMessageAvatar,
    CMiniMessageAttachments
  },
  props: {
    message: { type: Object, required: true },
    ownedDealers: {
      type: Array,
      default() {
        return []
      }
    },
    inMiniThread: { type: Boolean, required: false, default: false },
    group: { type: Number, required: false, default: null },
    highlightId: { type: Number, required: false, default: null },
    participant: { type: Object, required: true },
    owner: { type: Object, required: true },
    isManager: { type: Boolean, default: false },
    isSystem: { type: Boolean, default: false },
    isMine: { type: Boolean, default: false },
    showDate: { type: Boolean, default: false },
    showReport: { type: Boolean, default: false, required: false },
    avatar: { type: String, default: 'noavatar', required: false }, // if set also means this is the last message in group
    q: { type: String, required: false, default: '' }
  },
  data() {
    return {
      readyForDelete: false,
      deleted: false,
      icons: {
        trash: ciTrash,
        exclamationTriangle: ciExclamationTriangle
      },
      allowedImages: ['image/jpeg', 'image/jpg', 'image/png'],
      deleteConfirmationModalVisible: false
    }
  },
  computed: {
    ...mapState(USER_NS, {
      userId: 'id'
    }),
    ...mapGetters(USER_AGENT_NS, {
      isMobile: 'isMobile',
      isPc: 'isPc'
    }),
    deleteMessageModalBodyText() {
      return (
        this.$t('are you sure you want to delete this message?') +
        ' ' +
        this.$t('the message will be deleted only from your account') +
        '. ' +
        this.$t('the deletion is permanent and cannot be reverted')
      )
    },
    messageAttachments() {
      if (this.message.attachments && this.message.attachments.length) {
        return this.message.attachments
      }
      if (this.message.files && this.message.files.length) {
        return this.message.files
      }

      return []
    },
    daysFromNow() {
      return capitalizeString(
        timeDistanceFromNow(this.message.created, { locale: this.$i18n.locale })
      )
    },
    humanDate() {
      return format(this.message.created, 'P HH:mm')
    },
    senderName() {
      let sName = this.participant.display_name
      const oid = this.owner.id
      if (oid === this.message.sender_user_id) {
        if (this.isManager && this.userId !== oid) {
          sName = this.owner.display_name
        } else {
          sName = this.$t('you')
        }
      }
      return sName
    },
    receiverName() {
      let rName = this.participant.display_name
      const oid = this.owner.id
      if (oid !== this.message.sender_user_id) {
        if (this.isManager && this.userId !== oid) {
          rName = this.owner.display_name
        } else {
          rName = this.$t('you')
        }
      }
      return rName
    },
    messagePending() {
      return this.message.id === 'pendingmsg'
    },
    messageFailed() {
      return this.message.id === 'failed'
    },
    messageHour() {
      return format(this.message.created, 'HH:mm', {
        locale: this.$i18n.locale
      })
    },
    messageFullDate() {
      return format(this.message.created, 'd/L/yyyy HH:mm', {
        locale: this.$i18n.locale
      })
    },
    participantDeleted() {
      return this.participant.is_deleted
    },
    participantNameToDisplay() {
      if (this.participantDeleted) {
        return '[' + this.$t('deleted::msg_deleted') + ']'
      }

      return this.message.name
    },
    finalMessage() {
      let msgBody = this.message.body

      msgBody = this.urlify(msgBody)

      if (this.q) {
        // we have a search query that appears in one (or more) of these messages
        // so we have to find if the message body contains any of this, and add a span around it in order to highlight it
        const lowerBody = msgBody.toLowerCase() // we need the lowercase in order to not be case sensitive
        const indexArray = []
        let i = -1
        const inputText = this.q.toLowerCase()

        while ((i = lowerBody.indexOf(inputText, i + 1)) >= 0)
          indexArray.push(i)

        // so we have an array of indexes of where the query exists
        // do a for loop and replace them  with spans

        let highlightClass = 'highlight'
        if (this.highlightId === this.message.id) {
          highlightClass = 'highlight actual'
        }
        for (let j = 0; j < indexArray.length; j++) {
          const index = indexArray[j]
          const span = "<span class='" + highlightClass + "'>"
          const spanEnd = '</span>'
          msgBody =
            msgBody.substring(0, index) +
            span +
            msgBody.substring(index, index + inputText.length) +
            spanEnd +
            msgBody.substring(index + inputText.length)

          if (indexArray[j + 1]) {
            // we just added a span, so the next index should be what it was PLUS the size of
            // the span strings that were added before, that's why i multiply the length of span
            // with the j+1 (because this gets increased exponentially in every loop)

            indexArray[j + 1] =
              indexArray[j + 1] + (j + 1) * (span.length + spanEnd.length)
          }
        }
      }

      return msgBody
    },
    messageCreated() {
      return format(this.message.created, 'EEEE d/L/yyyy', {
        locale: this.$i18n.locale
      })
    }
  },
  methods: {
    urlify(txt) {
      if (this.isSystem) {
        return txt
      }

      const urlRegex = /((?:https?:\/\/)?(?:[\w.-]+\.)?(car|plot)\.gr\/[^\s<>"]*[^.,!?<>\s])/gm

      return txt.replace(urlRegex, function(url) {
        let urlToGo = url
        if (urlToGo.match(/^http(s)?:\/\//g) === null) {
          urlToGo = '//' + url
        }
        return '<a href="' + urlToGo + '" target="_blank">' + url + '</a>'
      })
    },
    showDeleteModal() {
      this.readyForDelete = true
      this.deleteConfirmationModalVisible = true
    },
    deleteThisMessage() {
      if (this.readyForDelete && !this.deleted) {
        this.$emit('delete-msg', this.message.id)
        // this.deleted = true;
      }
    },
    reportClicked() {
      this.$emit('report-msg', this.message.id)
    }
  }
})
